import React, { useRef } from 'react';

import { compareBoardColumns } from '@aline/common';
import { link } from '@aline/routes';
import { Dialog, IconButton, Typography } from '@mui/material';
import { Formik } from 'formik';
import { LayoutDashboard, Minus, Users, X } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { AlineInput } from 'src/AlineInput';
import {
  AlineAvatar,
  AlineAvatarGroup,
  AlineButton,
  AlineTextarea,
  AlineTooltip,
} from 'src/components';
import { PICKER_CHIP_ICON_SIZE, usePickerChipStyles } from 'src/components/generic';
import { LucideIconRenderer } from 'src/components/generic/LucideIconRenderer';
import { AssigneePickerPopover } from 'src/components/workflow/AssigneePickerPopover';
import {
  DocumentPickerPopover,
  SelectedDocumentChips,
} from 'src/components/workflow/DocumentPickerPopover';
import { getColumnPositionInCategory } from 'src/components/workflow/phase-column-helpers';
import {
  PriorityDisplay,
  WorkflowPriority,
} from 'src/components/workflow/workflow-list/cells/WorkflowPriority';
import { ColumnPickerPopover } from 'src/components/workflow/WorkflowBoardRelocationDialog';
import { Apollo } from 'src/gql/generated';
import { useCurrentUserQuery } from 'src/gql/hooks/user';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

import { BoardList } from './board-toolbar/BoardFilterPicker';
import type { BoardOption } from './board-toolbar/BoardFilterPicker';
import { ProgressiveColumnIcon } from './settings/ColumnItem';
import type { AssigneeOption } from '../AssigneePickerPopover';
import type { SelectedDocument } from '../DocumentPickerPopover';

/** The chip sits in a dense footer row, so extra assignees collapse into a +N bubble. */
const ASSIGNEE_AVATAR_MAX = 3;

interface CreateWorkflowFormValues {
  assigneeIds: string[];
  boardId: string | null;
  columnId: string | null;
  description: string;
  documents: SelectedDocument[];
  priority: Apollo.WorkflowPriority | null;
  title: string;
}

const CreateWorkflowSchema = Yup.object().shape({
  assigneeIds: Yup.array().of(Yup.string().required()).default([]),
  boardId: Yup.string().nullable(),
  columnId: Yup.string().nullable().required('Please select a status column'),
  description: Yup.string().default(''),
  documents: Yup.array().default([]),
  priority: Yup.string()
    .nullable()
    .oneOf([...Object.values(Apollo.WorkflowPriority), null]),
  title: Yup.string().trim().required('Title is required'),
});

interface CreateWorkflowInput {
  redirectToSigning: boolean;
  values: CreateWorkflowFormValues;
}

function useCreateWorkflow() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [initializeWorkflow, { loading }] = Apollo.useInitializeWorkflowMutation();

  const createWorkflow = async ({ redirectToSigning, values }: CreateWorkflowInput) => {
    const documentIds = values.documents.map(d => d.id);
    const { data } = await initializeWorkflow({
      variables: {
        input: {
          assigneeIds: values.assigneeIds,
          documentIds,
          props: {
            columnId: values.columnId ?? undefined,
            description: values.description || undefined,
            name: values.title,
            priority: values.priority ?? undefined,
          },
        },
      },
    });

    const newWorkflowId = data?.initializeWorkflow?.workflow?.id;
    if (!newWorkflowId) {
      enqueueSnackbar('Failed to create workflow', { variant: 'error' });
      return null;
    }

    enqueueSnackbar('Workflow created successfully', { variant: 'success' });
    if (redirectToSigning) {
      navigate(link.SendForSignatureWorkflow({ workflowId: newWorkflowId }));
    } else {
      navigate(link.WorkflowDetail({ workflowId: newWorkflowId }));
    }
    return newWorkflowId;
  };

  return { createWorkflow, isCreating: loading };
}

const useStyles = makeStyles()(theme => ({
  attachFilesSection: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    width: '100%',
  },
  closeButton: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.5),
  },
  descriptionInput: {
    '&&': {
      border: 'none',
      padding: theme.spacing(0.5, 0),
    },
    '&&:focus': {
      border: 'none',
      outline: 'none',
    },
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    height: '100%',
    width: '100%',
  },
  dialogPaper: {
    alignItems: 'flex-start',
    background: '#FFF',
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    maxWidth: 970,
    padding: 32,
    width: 970,
  },
  footer: {
    alignItems: 'center',
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: theme.spacing(2),
    width: '100%',
  },
  footerLeft: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  footerRight: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.5),
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    width: '100%',
  },
  headerRow: {
    alignItems: 'flex-start',
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  inputBase: {
    '&::placeholder': {
      fontSize: theme.typography.pxToRem(24),
      fontWeight: 700,
    },
    fontSize: theme.typography.pxToRem(24),
    fontWeight: 700,
    padding: theme.spacing(0.5, 0),
  },
  titleInputBorder: {
    '&&': {
      border: 'none',
      minHeight: 'unset',
      padding: 0,
    },
    '&&:focus-within': {
      border: 'none',
    },
  },
  titleInputWrapper: {
    flex: 1,
  },
}));

interface CreateWorkflowDialogProps {
  onClose: () => void;
  open: boolean;
  /** Pre-select a board when opening the dialog */
  initialBoardId?: string;
  /** Pre-select a column when opening the dialog */
  initialColumnId?: string;
}

export function CreateWorkflowDialog({
  initialBoardId,
  initialColumnId,
  onClose,
  open,
}: CreateWorkflowDialogProps): JSX.Element {
  const { classes } = useStyles();
  const { currentUser } = useCurrentUserQuery();
  const { data: boardsData } = Apollo.useGetBoardsQuery();
  const defaultBoardId = initialBoardId ?? currentUser?.org?.defaultBoard?.id ?? null;

  return (
    <Dialog
      fullWidth
      classes={{ paper: classes.dialogPaper }}
      maxWidth="md"
      open={open}
      onClose={onClose}
    >
      <CreateWorkflowDialogContent
        boards={boardsData?.boards ?? []}
        defaultBoardId={defaultBoardId}
        initialColumnId={initialColumnId ?? null}
        onClose={onClose}
      />
    </Dialog>
  );
}

interface CreateWorkflowDialogContentProps {
  boards: NonNullable<Apollo.GetBoardsQuery['boards']>;
  defaultBoardId: string | null;
  initialColumnId: string | null;
  onClose: () => void;
}

/** Get the board's leftmost column, matching the order the board renders them in */
function getFirstColumnId(
  boards: NonNullable<Apollo.GetBoardsQuery['boards']>,
  boardId: string | null
): string | null {
  if (!boardId) return null;
  const board = boards.find(b => b.id === boardId);
  if (!board?.columns?.length) return null;
  const sortedColumns = [...board.columns].sort(compareBoardColumns);
  return sortedColumns[0]?.id ?? null;
}

interface SubmitDisabledReasonInput {
  columnId: string | null;
  isCreating: boolean;
  isValid: boolean;
  title: string;
}

/**
 * Why the footer buttons are blocked, doubling as their tooltip copy. Formik only fills `errors`
 * once it has validated, so the required fields are checked here to keep the buttons and the
 * explanation in step from the first render.
 */
function getSubmitDisabledReason({
  columnId,
  isCreating,
  isValid,
  title,
}: SubmitDisabledReasonInput): string {
  if (isCreating) return 'Creating workflow...';
  if (!title.trim()) return 'Add a title to continue';
  if (!columnId) return 'Select a status column to continue';
  if (!isValid) return 'Fill in the required fields to continue';
  return '';
}

function CreateWorkflowDialogContent({
  boards,
  defaultBoardId,
  initialColumnId,
  onClose,
}: CreateWorkflowDialogContentProps): JSX.Element {
  const { classes } = useStyles();
  const redirectToSigningRef = useRef(false);
  const { getPickerChipClassName } = usePickerChipStyles();
  const { createWorkflow, isCreating } = useCreateWorkflow();

  // Default to first column of the board if no initial column specified
  const defaultColumnId = initialColumnId ?? getFirstColumnId(boards, defaultBoardId);
  const initialValues: CreateWorkflowFormValues = {
    assigneeIds: [],
    boardId: defaultBoardId,
    columnId: defaultColumnId,
    description: '',
    documents: [],
    priority: null,
    title: '',
  };

  return (
    <Formik<CreateWorkflowFormValues>
      enableReinitialize
      initialValues={initialValues}
      validationSchema={CreateWorkflowSchema}
      onSubmit={async (values, { resetForm }) => {
        // Consume the signing intent up front so a throw below cannot leave it set.
        const redirectToSigning = redirectToSigningRef.current;
        redirectToSigningRef.current = false;

        const workflowId = await createWorkflow({ redirectToSigning, values });
        if (workflowId) {
          resetForm();
          onClose();
        }
      }}
    >
      {({ errors, handleSubmit, isValid, resetForm, setFieldValue, touched, values }) => {
        const selectedBoard = boards.find(b => b.id === values.boardId);
        const sortedColumns = [...(selectedBoard?.columns ?? [])].sort(compareBoardColumns);
        const submitDisabledReason = getSubmitDisabledReason({
          columnId: values.columnId,
          isCreating,
          isValid,
          title: values.title,
        });

        return (
          <form className={classes.dialogContent} onSubmit={handleSubmit}>
            {/* Header Section */}
            <div className={classes.header}>
              <div className={classes.headerRow}>
                <AlineInput
                  hideHelperText
                  className={classes.titleInputWrapper}
                  classes={{ input: classes.inputBase }}
                  inputClassName={classes.titleInputBorder}
                  placeholder="Type a title..."
                  value={values.title}
                  onChange={e => setFieldValue('title', e.target.value)}
                />
                <IconButton
                  className={classes.closeButton}
                  size="small"
                  onClick={() => {
                    resetForm();
                    onClose();
                  }}
                >
                  <X size={20} />
                </IconButton>
              </div>
              <AlineTextarea
                className={classes.descriptionInput}
                maxRows={5}
                minRows={3}
                placeholder="Add description..."
                value={values.description}
                onChange={e => setFieldValue('description', e.target.value)}
              />
            </div>

            {/* Attach Files Section */}
            <div className={classes.attachFilesSection}>
              <SelectedDocumentChips
                documents={values.documents}
                onRemove={id =>
                  setFieldValue(
                    'documents',
                    values.documents.filter(d => d.id !== id)
                  )
                }
              />
              <DocumentPickerPopover
                selectedDocuments={values.documents}
                onDocumentsChange={documents => setFieldValue('documents', documents)}
              />
            </div>

            {/* Footer Section */}
            <div className={classes.footer}>
              <div className={classes.footerLeft}>
                {/* Board Selector */}
                <BoardList
                  boards={boards}
                  renderTrigger={(currentBoard, onClick, isOpen) => (
                    <div className={getPickerChipClassName(isOpen)} onClick={onClick}>
                      <BoardChipContent board={currentBoard} />
                    </div>
                  )}
                  selectedBoardId={values.boardId ?? undefined}
                  showEditAction={false}
                  onBoardChange={boardId => {
                    setFieldValue('boardId', boardId);
                    // Default to first column of the new board
                    setFieldValue('columnId', getFirstColumnId(boards, boardId));
                  }}
                />

                {/* Assignees */}
                <AssigneePickerPopover
                  renderTrigger={(selectedAssignees, isOpen) => (
                    <div className={getPickerChipClassName(isOpen)}>
                      <AssigneeChipContent assignees={selectedAssignees} />
                    </div>
                  )}
                  selectedAssigneeIds={values.assigneeIds}
                  onAssigneesChange={assigneeIds => setFieldValue('assigneeIds', assigneeIds)}
                />

                {/* Status/Column Selector */}
                {sortedColumns.length > 0 && (
                  <ColumnPickerPopover
                    columns={sortedColumns}
                    renderTrigger={(column, isOpen) => (
                      <div className={getPickerChipClassName(isOpen)}>
                        <ColumnChipContent
                          column={column}
                          columns={sortedColumns}
                          errorMessage={touched.columnId ? errors.columnId : undefined}
                        />
                      </div>
                    )}
                    selectedColumnId={values.columnId}
                    onColumnChange={columnId => setFieldValue('columnId', columnId)}
                  />
                )}

                {/* Priority */}
                <WorkflowPriority
                  priority={values.priority}
                  renderTrigger={priority => <PriorityDisplay priority={priority} />}
                  onPriorityChange={priority => void setFieldValue('priority', priority)}
                />
              </div>

              <div className={classes.footerRight}>
                <AlineTooltip arrow placement="top" title={submitDisabledReason}>
                  <div>
                    <AlineButton
                      color="primary"
                      disabled={Boolean(submitDisabledReason)}
                      variant="outlined"
                      onClick={() => {
                        redirectToSigningRef.current = true;
                        handleSubmit();
                      }}
                    >
                      Start Signing
                    </AlineButton>
                  </div>
                </AlineTooltip>
                <AlineTooltip arrow placement="top" title={submitDisabledReason}>
                  <div>
                    <AlineButton
                      color="primary"
                      disabled={Boolean(submitDisabledReason)}
                      type="submit"
                      variant="contained"
                      onClick={() => {
                        redirectToSigningRef.current = false;
                      }}
                    >
                      Create
                    </AlineButton>
                  </div>
                </AlineTooltip>
              </div>
            </div>
          </form>
        );
      }}
    </Formik>
  );
}

function BoardChipContent({ board }: { board: BoardOption | undefined }): JSX.Element {
  const { pickerChipIconClassName, pickerChipLabelClassName } = usePickerChipStyles();

  if (!board) {
    return (
      <>
        <div className={pickerChipIconClassName}>
          <LayoutDashboard size={PICKER_CHIP_ICON_SIZE} />
        </div>
        <Typography className={pickerChipLabelClassName} variant="body2">
          Select Board
        </Typography>
      </>
    );
  }

  return (
    <>
      <div className={pickerChipIconClassName}>
        <LucideIconRenderer color={board.color} icon={board.icon} size={PICKER_CHIP_ICON_SIZE} />
      </div>
      <Typography noWrap className={pickerChipLabelClassName} variant="body2">
        {board.name}
      </Typography>
    </>
  );
}

function AssigneeChipContent({ assignees }: { assignees: AssigneeOption[] }): JSX.Element {
  const { pickerChipIconClassName, pickerChipLabelClassName } = usePickerChipStyles();

  if (!assignees.length) {
    return (
      <>
        <div className={pickerChipIconClassName}>
          <Users size={PICKER_CHIP_ICON_SIZE} />
        </div>
        <Typography className={pickerChipLabelClassName} variant="body2">
          Assignees
        </Typography>
      </>
    );
  }

  return (
    <>
      <AlineAvatarGroup max={ASSIGNEE_AVATAR_MAX} styles={{ overlap: 4, size: 'xxs' }}>
        {assignees.map(assignee => (
          <AlineTooltip key={assignee.id} arrow placement="top" title={assignee.displayName}>
            <AlineAvatar
              avatarInfo={assignee.displayName}
              styles={{ backgroundColor: assignee.avatarColor ?? undefined, size: 'xxs' }}
            />
          </AlineTooltip>
        ))}
      </AlineAvatarGroup>
      <Typography noWrap className={pickerChipLabelClassName} variant="body2">
        {assignees.length === 1 ? assignees[0].displayName : `${assignees.length} assignees`}
      </Typography>
    </>
  );
}

type ColumnChipOption = Pick<Apollo.Column, 'category' | 'color' | 'id' | 'name' | 'sortKey'>;

interface ColumnChipContentProps {
  column: ColumnChipOption | undefined;
  columns: ColumnChipOption[];
  /** Validation message shown in place of the label once the field has been touched. */
  errorMessage?: string;
}

function ColumnChipContent({ column, columns, errorMessage }: ColumnChipContentProps): JSX.Element {
  const { pickerChipIconClassName, pickerChipLabelClassName } = usePickerChipStyles();

  if (!column) {
    return (
      <>
        <div className={pickerChipIconClassName}>
          <Minus size={PICKER_CHIP_ICON_SIZE} />
        </div>
        <Typography
          className={pickerChipLabelClassName}
          color={errorMessage ? 'error' : 'inherit'}
          variant="body2"
        >
          {errorMessage ?? 'Status'}
        </Typography>
      </>
    );
  }

  const { index, total } = getColumnPositionInCategory(column, columns);

  return (
    <>
      <ProgressiveColumnIcon
        boxSize={PICKER_CHIP_ICON_SIZE}
        category={column.category}
        className={pickerChipIconClassName}
        color={column.color}
        iconSize={PICKER_CHIP_ICON_SIZE}
        index={index}
        showBackground={false}
        showBorder={false}
        total={total}
      />
      <Typography noWrap className={pickerChipLabelClassName} variant="body2">
        {column.name}
      </Typography>
    </>
  );
}
